import { XIcon } from 'lucide-react';

import {
    Button,
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogTitle,
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerDescription,
    DrawerTitle,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    cn,
} from 'shared-ui';

import { useAppConfig } from '@/config/AppConfigContext';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { UPSELL_PRODUCTS, type ProductKey, type UpsellFeature, type UpsellProductConfig } from './upsellContent';

interface UpsellDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Which product's upsell to show. Content is read from UPSELL_PRODUCTS. */
    product: ProductKey;
}

/** Icon-led benefit rows. Renders nothing when a product has no features yet. */
function FeatureList({ features }: { features: UpsellFeature[] }) {
    if (features.length === 0) return null;
    return (
        <ul className="flex flex-col gap-4">
            {features.map((feature) => (
                <li key={feature.title} className="flex items-start gap-3">
                    {/* Light/dark illustration swap mirrors the sidebar logo pattern. */}
                    <img src={feature.iconSrc} alt="" className="size-16 shrink-0 dark:hidden" />
                    <img src={feature.iconSrcDark} alt="" className="hidden size-16 shrink-0 dark:block" />
                    <div className="space-y-0.5">
                        <p className="text-foreground text-sm leading-5 font-bold tracking-normal">{feature.title}</p>
                        <p className="text-muted-foreground text-sm leading-5 font-normal tracking-normal">
                            {feature.body}
                        </p>
                    </div>
                </li>
            ))}
        </ul>
    );
}

/** Full-width primary CTA. Renders as a link when a contact email is configured. */
function CtaButton({ config, href }: { config: UpsellProductConfig; href?: string }) {
    return (
        <Button className="h-11 w-full" render={href ? <a href={href} /> : undefined}>
            {config.ctaLabel}
        </Button>
    );
}

/**
 * Upsell dialog shown when the active client does not have a product's module.
 *
 * Layout (responsive, mirroring SignOutDialog/HelpDialog's Dialog↔Drawer split):
 *   - Desktop: a modal with the hero image on the left and content on the right.
 *   - Mobile: a bottom drawer that stacks title → image → intro → features → CTA.
 *
 * Purely presentational and product-agnostic — all copy/imagery comes from UPSELL_PRODUCTS.
 */
export function UpsellDialog({ open, onOpenChange, product }: UpsellDialogProps) {
    const isDesktop = useMediaQuery('(min-width: 768px)');
    const { supportEmail } = useAppConfig();
    const config = UPSELL_PRODUCTS[product];
    // CTA routes to the Origin team via the shared support email until a self-serve upgrade flow exists.
    const ctaHref = supportEmail ? `mailto:${supportEmail}` : undefined;

    if (isDesktop) {
        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent
                    className="max-h-[calc(100dvh-2rem)] min-h-[592px] w-[754px] overflow-hidden p-0 sm:max-w-[754px]"
                    showCloseButton={false}
                >
                    <div className="flex min-h-0 flex-col">
                        {/* Full-width 80px header bar: title left, close right (mirrors HelpDialog). */}
                        <header className="border-border flex h-20 shrink-0 items-center justify-between gap-4 border-b px-6">
                            <DialogTitle className="min-w-0 truncate text-2xl leading-none font-black tracking-wide uppercase">
                                {config.title}
                            </DialogTitle>
                            <Tooltip>
                                <DialogClose
                                    render={
                                        <TooltipTrigger
                                            render={
                                                <Button
                                                    variant="ghost"
                                                    size="icon-sm"
                                                    className="size-10 shrink-0 rounded-md"
                                                />
                                            }
                                        />
                                    }
                                >
                                    <XIcon className="size-4" strokeWidth={2.25} />
                                    <span className="sr-only">Close</span>
                                </DialogClose>
                                <TooltipContent side="top" align="center">
                                    Close
                                </TooltipContent>
                            </Tooltip>
                        </header>
                        {/* Body: image left, content right. 512px at the minimum dialog height, growing
                            with longer copy until the viewport cap, after which the content column scrolls. */}
                        <div className="flex min-h-0 grow">
                            {config.image && (
                                <div className="relative w-1/2 shrink-0">
                                    <img src={config.image} alt="" className="h-full w-full object-cover" />
                                    {config.marker && (
                                        <img
                                            src={config.marker.src}
                                            alt=""
                                            className={cn(config.marker.className, config.marker.desktopClassName)}
                                        />
                                    )}
                                    {config.logo && (
                                        // Product logo overlaid on the bottom half of the image.
                                        <div className="absolute inset-x-0 top-1/2 bottom-0 flex items-center justify-center">
                                            <img
                                                src={config.logo.src}
                                                alt={config.name}
                                                className={cn(
                                                    'object-contain',
                                                    config.logo.sizeClassName,
                                                    config.logo.desktopClassName,
                                                )}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className="flex flex-1 flex-col gap-5 overflow-y-auto p-8">
                                <DialogDescription className="text-foreground text-base leading-6 font-bold tracking-normal">
                                    {config.intro}
                                </DialogDescription>
                                <FeatureList features={config.features} />
                                <div className="mt-auto pt-2">
                                    <CtaButton config={config} href={ctaHref} />
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Drawer open={open} onOpenChange={onOpenChange}>
            <DrawerContent className="border-border border data-[vaul-drawer-direction=bottom]:mt-[100px] data-[vaul-drawer-direction=bottom]:h-[calc(100dvh-100px)] data-[vaul-drawer-direction=bottom]:max-h-[calc(100dvh-100px)]">
                <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pt-6 pb-6">
                    <div className="flex flex-row items-center justify-between gap-4">
                        <DrawerTitle className="text-foreground min-w-0 text-xl leading-7 font-black tracking-wide uppercase">
                            {config.title}
                        </DrawerTitle>
                        <DrawerClose asChild>
                            <Button variant="ghost" size="icon-sm" className="size-10 shrink-0 rounded-md">
                                <XIcon className="size-4" strokeWidth={2.25} />
                                <span className="sr-only">Close</span>
                            </Button>
                        </DrawerClose>
                    </div>
                    {config.image && (
                        <div className="relative -mx-4 mt-4 h-44 shrink-0 overflow-hidden">
                            <img src={config.image} alt="" className="h-full w-full object-cover" />
                            {config.marker && (
                                <img src={config.marker.src} alt="" className={config.marker.className} />
                            )}
                            {config.logo && (
                                // Product logo overlaid on the bottom half of the image.
                                <div className="absolute inset-x-0 top-1/2 bottom-0 flex items-center justify-center">
                                    <img
                                        src={config.logo.src}
                                        alt={config.name}
                                        className={cn('object-contain', config.logo.sizeClassName)}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    <DrawerDescription className="text-foreground mt-4 text-base leading-6 font-bold tracking-normal">
                        {config.intro}
                    </DrawerDescription>
                    <div className="mt-4">
                        <FeatureList features={config.features} />
                    </div>
                    <div className="mt-6">
                        <CtaButton config={config} href={ctaHref} />
                    </div>
                </div>
            </DrawerContent>
        </Drawer>
    );
}
